import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FarmProvider } from 'src/app/providers/farm.provider';
import { AssociationModel } from 'src/app/schemes/models/association.model';
import { FarmModel } from 'src/app/schemes/models/farm.model';
import { MilkingControlModel } from 'src/app/schemes/models/milking-control.model';
import { AssociationService } from 'src/app/services/association.service';
import { FarmService } from 'src/app/services/farm.service';
import { TimeService } from 'src/app/services/time.service';
import ngPrimeLocale from '../../../../../assets/locale/ngprime.json';

@Component({
	selector: 'app-filter-milking-controls-modal',
	templateUrl: './filter-milking-controls-modal.component.html',
	styleUrls: ['./filter-milking-controls-modal.component.scss'],
})
export class FilterMilkingControlsModalComponent implements OnInit {
	es = ngPrimeLocale;
	filters: { [key in keyof MilkingControlModel]?: any } = {};
	association: AssociationModel = {} as AssociationModel;
	currentFarm: FarmModel = {} as FarmModel;
	farms: { name: string; _id: string }[];
	filteredFarms: { name: string; _id: string }[];
	methods: { name: string; _id: string }[] = [];
	controllers: { name: string; _id: string }[] = [];
	urlToNavigate: string = '';

	constructor(
		private associationService: AssociationService,
		private farmProvider: FarmProvider,
		private dialogRef: MatDialogRef<FilterMilkingControlsModalComponent>,
		private router: Router,
		private datePipe: DatePipe,
		private farmService: FarmService,

		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.filters = {
			code: null,
			date: { start: null, end: null },
			farm: null,
			method: null,
			controller: null,
			alertCount: {
				start: null,
				end: null,
			},
		};
	}

	async ngOnInit(): Promise<void> {
		try {
			this.association = await this.associationService.getCurrentAssociation();
			this.currentFarm = await this.farmService.getCurrentFarm();
			await this.setFarms();
			await this.setMethods();
			await this.setControllers();
			this.urlToNavigate = this.data.url;
			this.setFiltersValues(this.data);
		} catch (error) {
			console.error(error);
		}
	}

	async setFarms(): Promise<void> {
		const farmsData = (await this.farmProvider.getAll({ _show: 'name' })).data;
		this.farms = farmsData.map(farm => ({ name: farm.name, _id: farm._id }));
		if (this.currentFarm) {
			this.farms = this.farms.filter(farm => farm._id === this.currentFarm._id);
		}
		this.filteredFarms = this.farms;
	}

	async setMethods(): Promise<void> {
		const methodsData = this.association.config?.milkingData?.milkingMethods || [];
		this.methods = methodsData.map(method => ({ name: method, _id: method }));
	}

	async setControllers(): Promise<void> {
		const controllersData = this.association.config?.milkingData?.milkingControllers || [];
		this.controllers = controllersData.map(controller => ({ name: controller, _id: controller }));
	}

	setFiltersValues(data: any): void {
		if (data.filters) {
			if (data.filters.code_like) this.filters.code = data.filters.code_like;
			if (data.filters.farm_in) {
				const prevFilterFarms: {}[] = [];
				data.filters.farm_in = data.filters.farm_in.split(',');
				this.filters.farm = data.filters.farm_in.forEach(farmData =>
					this.farms.find(farm => {
						if (farm._id === farmData) {
							prevFilterFarms.push(farm);
						}
						return null;
					})
				);
				this.filters.farm = prevFilterFarms;
			}
			if (data.filters.method_in) {
				const prevFilterMethods: {}[] = [];
				data.filters.method_in = data.filters.method_in.split(',');
				this.filters.method = data.filters.method_in.map(methodData =>
					this.methods.find(method => {
						if (method._id === methodData) {
							prevFilterMethods.push(method);
						}
						return null; // Add a return statement here
					})
				);
				this.filters.method = prevFilterMethods;
			}

			if (data.filters.controller_in) {
				const prevFilterControllers: {}[] = [];
				data.filters.controller_in = data.filters.controller_in.split(',');
				this.filters.controller = data.filters.controller_in.forEach(controllerData =>
					this.controllers.find(controller => {
						if (controller._id === controllerData) {
							prevFilterControllers.push(controller);
						}
						return null;
					})
				);
				this.filters.controller = prevFilterControllers;
			}

			if (data.filters.alertCount_gte) this.filters.alertCount.start = data.filters.alertCount_gte;
			if (data.filters.alertCount_lte) this.filters.alertCount.end = data.filters.alertCount_lte;

			if (data.filters.date_gte) this.filters.date.start = TimeService.parseDate(data.filters.date_gte);
			if (data.filters.date_lte) this.filters.date.end = TimeService.parseDate(data.filters.date_lte);
		}
	}

	parseFilters(filters: Object) {
		return Object.entries(filters).reduce((newFilters, [key, value]) => {
			if (!value) {
				return newFilters;
			}
			if (key.toLowerCase().includes('date')) {
				if (value.start) newFilters[`${key}_gte`] = (value.start as Date).toISOString();
				if (value.end) {
					newFilters[`${key}_lte`] = (value.end as Date).toISOString();
				}
			} else if (key.toLowerCase().includes('alertcount')) {
				if (value.start) newFilters[`${key}_gte`] = +value.start;
				if (value.end) {
					newFilters[`${key}_lte`] = +value.end;
				}
			} else if (typeof value === 'string') {
				newFilters[`${key}_like`] = value;
			} else if (Array(value) && value.length > 0) {
				newFilters[`${key}_in`] = value.map(val => val._id).join(',') || '';
			} else {
				if (typeof value === 'object') {
					for (const val in value) {
						newFilters[`${key}.${val}_like`] = filters[key][val];
					}
				}
				if (!value.value) {
					return newFilters;
				}
				newFilters[`${key}`] = value.value;
			}
			return newFilters;
		}, {});
	}

	public onSaveFilters(): void {
		const urlFilters = this.parseFilters(this.filters);
		// urlFilters['active'] = this.active;
		this.dialogRef.close(urlFilters);
		this.router.navigate([this.urlToNavigate], { queryParams: urlFilters });
	}

	public onClearFilters(): void {
		this.filters = {
			code: null,
			date: { start: null, end: null },
			farm: null,
			method: null,
			controller: null,
			alertCount: { start: null, end: null },
		};
	}
}
